import {
  IOpenxBidRequest,
  IPubmaticBidRequest,
  IRubiconBidRequest,
  IAppNexusBidRequest,
  IIXVideoBidRequest,
  IOneVideoBidRequest,
  IPulsepointBidRequest,
  IEmxDigitalBidRequest
} from '../../../typings/IPrebid'
import {
  DEFAULT_MAX_DURATION,
  DEFAULT_VIDEO_AD_HEIGHT,
  DEFAULT_VIDEO_AD_WIDTH,
  VIDEO_DESKTOP_FLOOR,
  VIDEO_MOBILE_FLOOR,
  OPENX_URL,
  TEST_BID_FLOOR
} from '../../constants/video'
import {
  Appnexus,
  EmxDigital,
  IndexExchangeVideo,
  OneVideo,
  OpenX,
  Pubmatic,
  Rubicon,
  Pulsepoint
} from '../../enums/VideoBidder'
import {
  API,
  DeliveryMethod,
  Linearity,
  Placement,
  PlaybackMethod,
  Protocol
} from '../../enums/OpenRTB'
import { Bidder } from '../../enums/index'
import { PENNY_PRECISION } from '../../constants/index'
import { VideoBidder } from '../../enums/Bidder'
import { IVideoAdTagOptions } from '../../../typings/IVideoOptions'
import { Flooring } from '../../flooring/Flooring'

export default class VideoBidRequest {
  public readonly options: IVideoAdTagOptions
  private readonly webModel: IWebViewModel
  public readonly overrides: any
  private playerHeight: number
  private playerWidth: number
  private floor: number

  constructor(
    options: IVideoAdTagOptions,
    webmodel: IWebViewModel,
    overrides: any = {}
  ) {
    this.options = options
    this.overrides = overrides
    this.webModel = webmodel
    this.playerHeight = this.options.contentHeight || DEFAULT_VIDEO_AD_HEIGHT
    this.playerWidth = this.options.contentWidth || DEFAULT_VIDEO_AD_WIDTH
    this.floor = Flooring.getVideoFloor({ country: webmodel.country })
  }

  // Uses discrepancies and gross bid adjustments to calculate the bidFloor for a particular bidder.
  private calcFloor(bidderCode: VideoBidder, grossAdjustment = 1): number {
    const discrepancies = this.options.discrepencies || {}
    const discrepancy = discrepancies[bidderCode] || 1

    const bidFloor = roundNumberToPrecision(
      this.floor / grossAdjustment / discrepancy,
      PENNY_PRECISION
    )

    return bidFloor
  }

  get appnexus(): IAppNexusBidRequest {
    let placementId
    const appNexusGrossAdjustment = 0.85

    if (this.options.testBids.appnexus) {
      placementId = Appnexus.testPlacementId
    } else if (this.isMobileAutoplay()) {
      placementId = this.options.appnexusVideoAdhesionId
    } else if (this.options.mobile) {
      placementId = this.options.appnexusVideoMobileId
    } else {
      placementId = this.options.appnexusVideoDesktopId
    }

    const bidFloor = this.calcFloor(
      VideoBidder.appnexus,
      appNexusGrossAdjustment
    )

    return {
      bidder: Bidder.appnexus,
      params: {
        placementId,
        reserve: bidFloor,
        video: {
          frameworks: [API.VPAID_2_0],
          id: this.options.adunitId,
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          playback_method: this.options.autoplay
            ? ['auto_play_sound_off']
            : ['click_to_play'],
          skippable: true,
          startdelay: 0
        }
      }
    }
  }

  get emxDigital(): IEmxDigitalBidRequest {
    let placementId

    if (this.options.testBids.emxDigital) {
      placementId = EmxDigital.testPlacementId
    } else if (this.isMobileAutoplay()) {
      placementId = EmxDigital.mobileAutoplayId
    } else if (this.options.sticky || this.isDesktopAutoplay()) {
      placementId = EmxDigital.desktopAutoplayId
    } else if (this.options.mobile) {
      placementId = EmxDigital.mobileCtpId
    } else {
      placementId = EmxDigital.desktopCtpId
    }

    const bidFloor = this.calcFloor(VideoBidder.emx).toString()

    return {
      bidder: VideoBidder.emx,
      params: {
        tagid: placementId,
        bidfloor: bidFloor,
        video: {
          frameworks: [API.VPAID_2_0],
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          playback_method: this.options.autoplay
            ? ['auto_play_sound_off']
            : ['click_to_play'],
          skippable: true,
          startdelay: 0
        }
      }
    }
  }

  get indexExchange(): IIXVideoBidRequest {
    const test = this.options.testBids.ixVideo
    let siteId

    if (test) {
      siteId = IndexExchangeVideo.testSiteId
    } else if (this.options.mobile) {
      siteId = this.options.autoplay
        ? IndexExchangeVideo.siteIdAutoplayMobile
        : IndexExchangeVideo.siteIdCTPMobile
    } else {
      siteId = this.options.autoplay
        ? IndexExchangeVideo.siteIdAutoplayDesktop
        : IndexExchangeVideo.siteIdCTPDesktop
    }

    // https://knowledgebase.indexexchange.com/display/VIDEO/Parameters
    return {
      bidder: VideoBidder.indexExchange,
      params: {
        api: [API.VPAID_1_0, API.VPAID_2_0],
        bidFloor: this.calcFloor(VideoBidder.indexExchange),
        bidFloorCurrency: 'USD',
        debug: test,
        delay: 0,
        deliveryMethods: [DeliveryMethod.progressive],
        height: this.playerHeight,
        maxduration: DEFAULT_MAX_DURATION,
        mimes: this.options.mimes,
        protocols: [
          Protocol.VAST_2_0,
          Protocol.VAST_3_0,
          Protocol.VAST_2_0_wrapper,
          Protocol.VAST_3_0_wrapper,
          Protocol.VAST_4_0,
          Protocol.VAST_4_0_wrapper
        ],
        referrer: top.window.location.href,
        secure: top.window.location.protocol === 'https:',
        siteId,
        skippable: true,
        timeout: IndexExchangeVideo.timeout,
        width: this.playerWidth
      }
    }
  }

  get oneVideo(): IOneVideoBidRequest {
    let pubId
    let site
    // note: bidfloor is undocumented, but in their adapter code.
    // don't know if they want a string or a number.
    let bidFloor
    const oneVideoPennyAdjustment = 0.01

    if (this.options.testBids.oneVideo) {
      pubId = OneVideo.testPublisherId
      site = {
        id: OneVideo.testSiteId,
        page: OneVideo.testPage,
        referrer: OneVideo.testReferrer
      }
      bidFloor = TEST_BID_FLOOR
    } else {
      pubId = OneVideo.publisherId
      site = {
        page: top.window.location.href
      }
      bidFloor = this.calcFloor(VideoBidder.oneVideo) + oneVideoPennyAdjustment
    }

    return {
      bidder: VideoBidder.oneVideo,
      params: {
        bidfloor: bidFloor,
        pubId,
        site,
        video: {
          api: [API.VPAID_2_0],
          delivery: [DeliveryMethod.progressive],
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          playerHeight: this.playerHeight,
          playerWidth: this.playerWidth,
          position: 1,
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ]
        }
      }
    }
  }

  get openx(): IOpenxBidRequest {
    const { domain: url } = this.options
    let unit

    if (this.isMobileAutoplay()) {
      unit = OpenX.adhesionUnitId
    } else if (this.options.mobile) {
      unit = OpenX.mobileUnitId
    } else if (this.options.autoplay) {
      unit = OpenX.desktopAutoplayUnitId
    } else {
      unit = OpenX.desktopCTPUnitId
    }

    const openxPennyAdjustment = 0.01
    const customFloor = this.calcFloor(VideoBidder.openx) + openxPennyAdjustment
    // TODO: Remove `video.aumfs` when OpenX fixes their adapter to handle customFloor on videos correctly.
    const aumfsFloor = (Math.round(customFloor * 100) / 100) * 1000
    return {
      bidder: VideoBidder.openx,
      params: {
        customFloor,
        delDomain: OPENX_URL,
        openrtb: {
          imp: [
            {
              video: {
                maxduration: DEFAULT_MAX_DURATION
              }
            }
          ]
        },
        unit,
        video: {
          aumfs: aumfsFloor,
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          url,
          vtest: this.options.testBids.openx ? 1 : 0
        }
      }
    }
  }

  get pubmatic(): IPubmaticBidRequest {
    const wh = `${this.playerWidth}x${this.playerHeight}`
    let publisherId = Pubmatic.publisherId
    let adSlot

    if (this.options.testBids.pubmatic) {
      publisherId = Pubmatic.testPublisherId
      adSlot = `${Pubmatic.testAdSlot}@${wh}`
    } else if (this.isMobileAutoplay()) {
      adSlot = `${Pubmatic.adSlotAdhesion}@${wh}`
    } else if (
      (!this.options.mobile && this.options.sticky) ||
      this.isDesktopAutoplay()
    ) {
      adSlot = `${Pubmatic.adSlotSticky}@${wh}`
    } else if (this.options.mobile) {
      adSlot = `${Pubmatic.adSlotMobile}@${wh}`
    } else {
      adSlot = `${Pubmatic.adSlotDesktop}@${wh}`
    }

    return {
      bidder: VideoBidder.pubmatic,
      params: {
        adSlot,
        kadfloor: this.calcFloor(VideoBidder.pubmatic).toString(),
        bcat: this.webModel.iabOptouts,
        publisherId,
        video: {
          api: [API.VPAID_2_0],
          delivery: [DeliveryMethod.progressive],
          linearity: Linearity.linear,
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          placement: Placement.stream,
          playbackmethod: this.options.autoplay
            ? [PlaybackMethod.autoplaySoundOff]
            : [PlaybackMethod.ctp],
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          skippable: true,
          startdelay: 0
        }
      }
    }
  }

  get pulsepoint(): IPulsepointBidRequest {
    const test = this.options.testBids.pulsepoint
    const publisherId = Pulsepoint.publisherId
    let siteId

    // // We don't have a test siteId yet for pulsepoint
    // if (test) {
    //   siteId = Pulsepoint.testSiteId
    // } else

    if (this.options.mobile) {
      siteId = this.options.autoplay
        ? Pulsepoint.siteIdAutoplayMobile
        : Pulsepoint.siteIdCTPMobile
    } else {
      siteId = this.options.autoplay
        ? Pulsepoint.siteIdAutoplayDesktop
        : Pulsepoint.siteIdCTPDesktop
    }

    return {
      bidder: VideoBidder.pulsepoint,
      params: {
        // TODO: uncomment when pubmatic fixes their floors.
        bidfloor: this.calcFloor(VideoBidder.pulsepoint),
        bcat: this.webModel.iabOptouts,
        cp: publisherId,
        ct: siteId,
        cf: '1X1',
        video: {
          api: [API.VPAID_2_0],
          delivery: [DeliveryMethod.progressive],
          linearity: Linearity.linear,
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          placement: Placement.stream,
          playbackmethod: this.options.autoplay
            ? [PlaybackMethod.autoplaySoundOff]
            : [PlaybackMethod.ctp],
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          skippable: true,
          startdelay: 0,
          w: this.playerWidth,
          h: this.playerHeight
        }
      }
    }
  }

  get rubicon(): IRubiconBidRequest {
    const test = this.options.testBids.rubicon
    const rubiconGrossAdjustment = 0.865
    let zoneId
    let floor

    if (test) {
      zoneId = Rubicon.testZone
      floor = TEST_BID_FLOOR
    } else if (this.options.mobile) {
      floor = this.calcFloor(VideoBidder.rubicon, rubiconGrossAdjustment)
      zoneId = this.options.autoplay
        ? Rubicon.zoneMobileAutoplay
        : Rubicon.zoneMobileCTP
    } else {
      floor = this.calcFloor(VideoBidder.rubicon, rubiconGrossAdjustment)
      zoneId = this.options.autoplay
        ? Rubicon.zoneDesktopAutoplay
        : Rubicon.zoneDesktopCTP
    }
    return {
      bidder: VideoBidder.rubicon,
      params: {
        accountId: (test ? Rubicon.testAccountId : Rubicon.accountId) as string,
        floor: test ? TEST_BID_FLOOR : floor,
        inventory: {
          category: this.options.categories.map((cat) =>
            cat.substr(0, Rubicon.categoryLength)
          )
        },
        siteId: (test ? Rubicon.testSizeId : Rubicon.siteId) as string,
        video: {
          aeParams: {
            'p_aso.video.ext.boxingallowed': '1'
          },
          api: [API.VPAID_2_0],
          language: 'en',
          linearity: Linearity.linear,
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          playerHeight: Math.round(this.playerHeight).toString(),
          playerWidth: Math.round(this.playerWidth).toString(),
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          size_id: Rubicon.sizeId,
          skipafter: 5,
          startdelay: 0
        },
        zoneId
      }
    }
  }
  private isMobileAutoplay(): boolean {
    return this.options.mobile && !!this.options.manualOptions.isAutomobile
  }
  private isDesktopAutoplay(): boolean {
    return !this.options.mobile && !!this.options.manualOptions.isAutoplay
  }
}

export function roundNumberToPrecision(num: number, precision: number): number {
  const power = Math.pow(10, precision)

  return Math.floor(num * power) / power
}
